// CardsView.js — Crews card-grid view + the crew card.
import React from 'react';
import { T } from '../lib/crewTokens';
import { hhmm, fmtMi } from '../lib/crewFormat';
import {
  CrewGlyph, OTBadge, TruckIcon, Avatar, ClockBadge, TicketProgress,
  ScheduleChip, DayRibbon, BudgetLegend, FuelGauge, VehicleFlags, statusAccent,
} from '../atoms/CrewAtoms';

const CC = T;

function CrewCard({ c, selected, onSelect, glyph }) {
  const isSel = selected && selected.id === c.id;
  const accent = statusAccent(c.status.level);
  const onCount = c.members.filter((m) => m.status === 'onsite').length;
  const noClockData = c.members.length > 0 && c.members.every((m) => m.status === 'unknown');
  return (
    <div onClick={() => onSelect(c)} className="crew-card-lift" style={{
      background: CC.surface, border: `1px solid ${isSel ? CC.green : CC.line}`, borderRadius: 13,
      boxShadow: isSel ? `0 0 0 2px ${CC.greenSoft}` : '0 1px 2px rgba(28,36,25,0.06)',
      cursor: 'pointer', display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* status hairline */}
      <div style={{ height: 3, background: accent, opacity: c.status.level === 'healthy' ? 0.4 : 1 }}></div>

      <div style={{ padding: '11px 13px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <CrewGlyph level={c.status.level} variant={glyph} size={19} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, color: CC.ink }}>{c.name}</div>
            <div style={{ fontSize: 10.5, color: CC.muteSoft }}>{c.foreman} · {c.division}</div>
          </div>
          <OTBadge ot={c.ot} dense />
        </div>

        {/* location */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, background: CC.surface2, border: `1px solid ${CC.lineSoft}`, borderRadius: 8, padding: '6px 9px' }}>
          <span style={{ width: 7, height: 7, borderRadius: 99, background: c.activity === 'driving' ? CC.orange : CC.green, flexShrink: 0, boxShadow: `0 0 0 3px ${c.activity === 'driving' ? CC.orangeSoft : CC.greenSoft}` }}></span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: CC.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.location.label}</div>
            <div style={{ fontSize: 9.5, color: CC.muteSoft }}>{c.activity === 'driving' ? `${c.remDriveMin}m drive · ` : ''}{c.location.addr}</div>
          </div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9.5, fontWeight: 700, color: CC.mute, fontFamily: CC.mono }}>
            <TruckIcon size={13} color={CC.mute} />{c.vehicle.no}
          </span>
        </div>

        {/* crew members */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex' }}>
            {c.members.slice(0, 5).map((m, i) => (
              <span key={i} style={{ marginLeft: i === 0 ? 0 : -7, zIndex: 5 - i, borderRadius: '50%', boxShadow: `0 0 0 2px ${CC.surface}` }}>
                <Avatar name={m.name} initials={m.initials} color={m.color} size={26} />
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: CC.ink }}>{noClockData ? '— no clock data' : `${onCount} on the clock`}</span>
            <span style={{ display: 'flex', gap: 4 }}>
              {c.members.some((m) => m.status === 'lunch') && <ClockBadge status="lunch" dense />}
              {c.members.some((m) => m.status === 'break') && <ClockBadge status="break" dense />}
              {!c.members.some((m) => m.status === 'lunch' || m.status === 'break') && (noClockData ? null : <ClockBadge status="onsite" since={c.clockIn} dense />)}
            </span>
          </div>
        </div>

        {/* tickets */}
        <TicketProgress done={c.ticketsDone} scheduled={c.ticketsScheduled} />

        {/* metric row */}
        <div style={{ display: 'flex', gap: 6 }}>
          <MiniStat label="Service left" value={`${Math.round(c.remServiceMin / 60 * 10) / 10}h`} sub={`${c.remaining} stops`} />
          <MiniStat label="Drive left" value={`${c.remDriveMin}m`} sub={fmtMi(c.remDriveMin / 2.1) + ' mi'} />
          <MiniStat label="Schedule" custom={<ScheduleChip behindMin={c.behindMin} dense />} />
        </div>

        {/* time on the clock */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
            <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: CC.muteSoft }}>Time today</span>
            {c.loc.flags > 0
              ? <span style={{ fontSize: 9.5, fontWeight: 700, color: CC.rust }}>{c.loc.flags} location {c.loc.flags > 1 ? 'flags' : 'flag'}</span>
              : <span style={{ fontSize: 9.5, fontWeight: 700, color: CC.green }}>on-site &amp; clean</span>}
          </div>
          <DayRibbon loc={c.loc} height={9} />
          <div style={{ marginTop: 5 }}><BudgetLegend budget={c.loc.budget} compact hideZero /></div>
        </div>

        {/* footer: vehicle + last ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingTop: 9, borderTop: `1px solid ${CC.lineSoft}` }}>
          <FuelGauge pct={c.vehicle.fuel} width={40} />
          <span style={{ width: 1, height: 14, background: CC.line }}></span>
          <div style={{ flex: 1, minWidth: 0 }}><VehicleFlags vehicle={c.vehicle} dense max={1} /></div>
          <span style={{ fontSize: 9.5, color: CC.muteSoft, whiteSpace: 'nowrap' }}>last · {c.lastTicket.at ? hhmm(c.lastTicket.at) : '—'}</span>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, sub, custom }) {
  return (
    <div style={{ flex: 1, background: CC.surface2, border: `1px solid ${CC.lineSoft}`, borderRadius: 8, padding: '6px 8px' }}>
      <div style={{ fontSize: 8.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: CC.muteSoft, marginBottom: 2 }}>{label}</div>
      {custom || (<><div style={{ fontSize: 14, fontWeight: 800, color: CC.ink, lineHeight: 1, fontFamily: CC.mono }}>{value}</div>{sub && <div style={{ fontSize: 9, color: CC.muteSoft, marginTop: 1 }}>{sub}</div>}</>)}
    </div>
  );
}

function CardsView({ rows, selected, onSelect, glyph }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(326px, 1fr))', gap: 12, paddingBottom: 8, alignContent: 'start' }}>
      {rows.map((c) => <CrewCard key={c.id} c={c} selected={selected} onSelect={onSelect} glyph={glyph} />)}
      {rows.length === 0 && <div style={{ padding: 40, textAlign: 'center', color: CC.muteSoft, fontSize: 12, gridColumn: '1/-1' }}>No crews match these filters.</div>}
    </div>
  );
}

export { CardsView, CrewCard };
